/**
 * Memoized drop-in replacement for @radix-ui/react-compose-refs (v1.1.2).
 *
 * Root cause: The original `composeRefs()` creates a NEW function on every
 * call.  Radix's `SlotClone` calls it inline during render, so each render
 * produces a callback ref with a different identity.  React 19 detects the
 * change, runs cleanup (sets refs to null via setState) then re-sets them
 * (setState again) — this happens synchronously during commit, causing
 * `TooltipTrigger` / `PopperAnchor` to infinite-loop.
 *
 * Fix: Cache composed refs in a nested WeakMap keyed on the input ref
 * identities.  Same inputs → same output function → stable identity →
 * React doesn't re-process the ref → no infinite loop.
 *
 * Installed via resolve.alias in webpack.renderer.config.js and
 * vite.web.config.ts so ALL Radix packages use this patched version.
 */

import * as React from 'react'

/* Sentinels for null / undefined (not valid WeakMap keys) */
const NULL_SENTINEL: object = Object.create(null)
const UNDEF_SENTINEL: object = Object.create(null)

function refKey(ref: unknown): object {
  if (ref === null) return NULL_SENTINEL
  if (ref === undefined) return UNDEF_SENTINEL
  return ref as object
}

function setRef(ref: unknown, value: unknown) {
  if (typeof ref === 'function') {
    return ref(value)
  } else if (ref !== null && ref !== undefined) {
    ;(ref as React.MutableRefObject<unknown>).current = value
  }
}

/**
 * Per-arity cache roots.
 *
 * Each arity gets its own chain of nested WeakMaps so that a 2-ref lookup
 * never collides with a 3-ref lookup at the same intermediate level.
 *
 *   arityCaches.get(2) → WeakMap<ref0, WeakMap<ref1, composedFn>>
 *   arityCaches.get(3) → WeakMap<ref0, WeakMap<ref1, WeakMap<ref2, composedFn>>>
 */
 
const arityCaches = new Map<number, WeakMap<object, any>>()

function composeRefs(...refs: unknown[]) {
  const n = refs.length
  if (n === 0) return () => {}

  /* Get or create the WeakMap chain root for this arity */
  let root = arityCaches.get(n)
  if (!root) {
    root = new WeakMap()
    arityCaches.set(n, root)
  }

  /* Walk through intermediate levels (refs[0] … refs[n-2]) */
   
  let level: WeakMap<object, any> = root
  for (let i = 0; i < n - 1; i++) {
    const key = refKey(refs[i])
    let next = level.get(key)
    if (!next) {
      next = new WeakMap()
      level.set(key, next)
    }
    level = next
  }

  /* Check leaf level (refs[n-1]) for a cached composed function */
  const lastKey = refKey(refs[n - 1])
  const cached = level.get(lastKey)
  if (cached) return cached

  /* Cache miss — build the composed callback (mirrors original logic) */
  const composed = (node: unknown) => {
    let hasCleanup = false
    const cleanups = refs.map((ref) => {
      const cleanup = setRef(ref, node)
      if (!hasCleanup && typeof cleanup === 'function') {
        hasCleanup = true
      }
      return cleanup
    })
    if (hasCleanup) {
      return () => {
        for (let i = 0; i < cleanups.length; i++) {
          const cleanup = cleanups[i]
          if (typeof cleanup === 'function') {
            cleanup()
          } else {
            setRef(refs[i], null)
          }
        }
      }
    }
  }

  level.set(lastKey, composed)
  return composed
}

function useComposedRefs(...refs: unknown[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return React.useCallback(composeRefs(...refs), refs)
}

export { composeRefs, useComposedRefs }
